import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useVenue } from "./VenueProvider";
import MetodoPagoField from "./shared/MetodoPagoField";
import { useAuth } from "./AuthProvider";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { normalizeDepositoEstado } from "../utils/depositoEstado";
import {
  assignProveedorToEvent,
  unassignProveedorFromEvent,
  setPlannerOnEvent,
  clearPlannerFromEvent,
  esSoloPlanner,
} from "../utils/proveedores";
import { costosDeEvento } from "../utils/costos";
import {
  proveedorDe,
  variantesDe,
  varianteDe,
  costoDeVariante,
  unidadDe,
} from "../utils/catalogo";
import { getValorEvento } from "../utils/valorEvento";
import {
  comisionPropuesta,
  comisionDeEvento,
  registrarComisionDeEvento,
  describeAcuerdo,
} from "../utils/comisiones";
import { fmtMXNInput, normalizeMoneyInput } from "../utils/money";
import { parseDateInputToTimestamp, todayInputValue, formatCalendarDate } from "../utils/dates";
import { showAlert } from "../alertService";
import { Button } from "./button";
import { Badge } from "./badge";
import { Alert } from "./alert";
import ProveedorCreateDialog from "./ProveedorCreateDialog";
import DocumentUploader from "./shared/DocumentUploader";
import {
  PlusIcon,
  TrashIcon,
  SparklesIcon,
  UserGroupIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleBottomCenterTextIcon,
  QuestionMarkCircleIcon,
  PencilSquareIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  BanknotesIcon,
  CheckIcon,
  ChevronRightIcon,
  PaperClipIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

// Un planner cobra comisión, no tarifa (FRA-355). Es una propiedad del
// proveedor, no del picker por el que se eligió, así que la pregunta se
// contesta en un solo lugar.
const esPlannerProveedor = (p) => p?.isPlanner === true || p?.categoria === "planner";

const formatCommentDate = (ts) => {
  if (!ts?.toDate) return "";
  return ts.toDate().toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtMoneyMXN = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "$0";
  return (
    "$" +
    Math.abs(n).toLocaleString("es-MX", {
      minimumFractionDigits: n % 1 !== 0 ? 2 : 0,
      maximumFractionDigits: 2,
    })
  );
};

const ProveedorEventDepositos = ({
  venueId,
  proveedorId,
  eventId,
  isViewer,
  soloLectura = false,
  onNavigateToDepositos,
}) => {
  const [depositos, setDepositos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!venueId || !proveedorId || !eventId || isViewer) {
      setDepositos([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(
        db,
        "venues",
        venueId,
        "proveedores",
        proveedorId,
        "depositos"
      ),
      where("eventId", "==", eventId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDepositos(snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, estado: normalizeDepositoEstado(data.estado) };
        }));
        setLoading(false);
      },
      (err) => {
        console.error("Proveedor event depositos error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [venueId, proveedorId, eventId, isViewer]);

  if (isViewer || loading) return null;

  const totals = depositos.reduce(
    (acc, d) => {
      const amt = Number(d.monto) || 0;
      if (d.estado === "activo") {
        acc.activo += amt;
      } else if (d.estado === "devuelto") {
        const devuelto = Number(d.montoDevuelto ?? d.monto) || 0;
        acc.devuelto += devuelto;
        acc.aplicado += Math.max(0, amt - devuelto);
      } else if (d.estado === "aplicado") {
        acc.aplicado += amt;
      }
      return acc;
    },
    { activo: 0, devuelto: 0, aplicado: 0 }
  );

  const hasAny = depositos.length > 0;

  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
      <span className="inline-flex items-center gap-1 text-zinc-500 font-medium">
        <BanknotesIcon className="h-3.5 w-3.5" />
        {hasAny ? "Depósitos:" : "Sin depósito para este evento"}
      </span>
      {totals.activo > 0 && (
        <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 font-semibold">
          {fmtMoneyMXN(totals.activo)} en custodia
        </span>
      )}
      {totals.devuelto > 0 && (
        <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-semibold">
          {fmtMoneyMXN(totals.devuelto)} devuelto
        </span>
      )}
      {totals.aplicado > 0 && (
        <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 font-semibold">
          {fmtMoneyMXN(totals.aplicado)} aplicado
        </span>
      )}
      {/* Sin depósito, el link solo sirve para registrar uno: en solo
          lectura no se ofrece. */}
      {onNavigateToDepositos && (hasAny || !soloLectura) && (
        <button
          type="button"
          onClick={onNavigateToDepositos}
          className="text-[#1b4d2e] hover:underline font-medium"
        >
          {hasAny ? "Ver detalle" : "Registrar depósito"}
        </button>
      )}
    </div>
  );
};

export const ProveedorEventComments = ({
  venueId,
  proveedorId,
  proveedorNombre,
  eventId,
  eventNombre,
  isViewer,
  // Solo lectura (inversionista, P-FRA-13): ve los comentarios, sin
  // escribir, editar ni borrar. `isViewer` sigue ocultando todo.
  soloLectura = false,
}) => {
  const { userInfo, user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    if (!venueId || !proveedorId || !eventId || isViewer) {
      setComments([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(
        db,
        "venues",
        venueId,
        "proveedores",
        proveedorId,
        "comentarios"
      ),
      where("eventId", "==", eventId),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Proveedor event comments error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [venueId, proveedorId, eventId, isViewer]);

  const handlePost = async () => {
    const texto = draft.trim();
    if (!texto || posting) return;
    setPosting(true);
    try {
      const ref = collection(
        db,
        "venues",
        venueId,
        "proveedores",
        proveedorId,
        "comentarios"
      );
      const docRef = await addDoc(ref, {
        texto,
        eventId,
        eventNombre: eventNombre || null,
        authorId: user?.uid || null,
        authorName: userInfo?.name || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_COMENTARIO_CREATE,
          entity: "proveedor_comentario",
          entityId: docRef.id,
          entityName: proveedorNombre || "",
          details: {
            proveedorId,
            proveedorNombre: proveedorNombre || "",
            eventId,
            eventNombre: eventNombre || null,
          },
        },
        userInfo,
        user
      );
      setDraft("");
      setComposing(false);
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    } finally {
      setPosting(false);
    }
  };

  const handleSaveEdit = async (id) => {
    const texto = editDraft.trim();
    if (!texto) {
      setEditingId(null);
      return;
    }
    try {
      await updateDoc(
        doc(
          db,
          "venues",
          venueId,
          "proveedores",
          proveedorId,
          "comentarios",
          id
        ),
        {
          texto,
          updatedAt: serverTimestamp(),
        }
      );
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_COMENTARIO_EDIT,
          entity: "proveedor_comentario",
          entityId: id,
          entityName: proveedorNombre || "",
          details: {
            proveedorId,
            proveedorNombre: proveedorNombre || "",
            eventId,
            eventNombre: eventNombre || null,
          },
        },
        userInfo,
        user
      );
      setEditingId(null);
      setEditDraft("");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    try {
      await deleteDoc(
        doc(
          db,
          "venues",
          venueId,
          "proveedores",
          proveedorId,
          "comentarios",
          deleteCandidate.id
        )
      );
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_COMENTARIO_DELETE,
          entity: "proveedor_comentario",
          entityId: deleteCandidate.id,
          entityName: proveedorNombre || "",
          details: {
            proveedorId,
            proveedorNombre: proveedorNombre || "",
            eventId,
            eventNombre: eventNombre || null,
          },
        },
        userInfo,
        user
      );
      setDeleteCandidate(null);
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  if (isViewer) return null;
  if (loading) return null;

  const count = comments.length;

  return (
    <div className="mt-2">
      <div className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 mb-2">
        <ChatBubbleBottomCenterTextIcon className="h-3.5 w-3.5" />
        {count === 0
          ? "Comentarios de este evento"
          : `${count} comentario${count === 1 ? "" : "s"} de este evento`}
      </div>

      <div className="pl-5 border-l-2 border-zinc-100 space-y-2">
        {count > 0 &&
            comments.map((c) => {
              const isEditing = editingId === c.id;
              return (
                <div
                  key={c.id}
                  className="rounded-lg bg-zinc-50/70 border border-zinc-100 p-2.5"
                >
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        autoFocus
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={3}
                        className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setEditDraft("");
                          }}
                          className="px-3 py-1 rounded-md text-xs font-medium text-zinc-600 hover:bg-zinc-100"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(c.id)}
                          className="px-3 py-1 rounded-md text-xs font-medium text-white"
                          style={{ backgroundColor: ACCENT }}
                        >
                          Guardar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-800 whitespace-pre-wrap">
                          {c.texto}
                        </div>
                        <div className="mt-1 text-xs text-zinc-400">
                          {c.authorName || "Anónimo"} ·{" "}
                          {formatCommentDate(c.createdAt)}
                          {c.updatedAt &&
                            c.createdAt &&
                            c.updatedAt.toMillis &&
                            c.createdAt.toMillis &&
                            c.updatedAt.toMillis() - c.createdAt.toMillis() >
                              1000 &&
                            " · editado"}
                        </div>
                      </div>
                      {!soloLectura && (
                      <div className="flex gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(c.id);
                            setEditDraft(c.texto || "");
                          }}
                          className="text-zinc-400 hover:text-zinc-600 p-1"
                          title="Editar"
                        >
                          <PencilSquareIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteCandidate(c)}
                          className="text-zinc-400 hover:text-red-500 p-1"
                          title="Eliminar"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

          {/* Inline compose — hidden until user clicks "Agregar comentario" */}
          {soloLectura ? (
            count === 0 && (
              <div className="text-xs text-zinc-400 italic">Sin comentarios.</div>
            )
          ) : composing ? (
            <div className="rounded-lg border border-zinc-200 bg-white p-2.5">
              <textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Escribe un comentario sobre este proveedor en este evento..."
                rows={2}
                className="w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setComposing(false);
                    setDraft("");
                  }}
                  className="px-3 py-1 rounded-md text-xs font-medium text-zinc-600 hover:bg-zinc-100"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handlePost}
                  disabled={!draft.trim() || posting}
                  className="px-3 py-1 rounded-md text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}
                >
                  {posting ? "Agregando..." : "Comentar"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setComposing(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1b4d2e] hover:underline"
            >
              <PlusIcon className="h-3 w-3" />
              Agregar comentario
            </button>
          )}
      </div>

      <Alert open={!!deleteCandidate} onClose={() => setDeleteCandidate(null)}>
        <div className="font-semibold mb-2">¿Eliminar este comentario?</div>
        <div className="mb-4 text-sm text-zinc-600">
          Esta acción no se puede deshacer.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setDeleteCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleDelete}>
            Eliminar
          </Button>
        </div>
      </Alert>
    </div>
  );
};

const HelpTooltip = ({ children }) => (
  <div className="relative group inline-flex">
    <QuestionMarkCircleIcon className="h-4 w-4 text-zinc-400 hover:text-zinc-600 cursor-help" />
    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block w-72 rounded-lg bg-zinc-800 text-white text-xs font-normal normal-case tracking-normal leading-relaxed p-3 shadow-lg z-50">
      {children}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-zinc-800"></div>
    </div>
  </div>
);

const SectionCard = ({ icon: Icon, title, helpText, children, action }) => (
  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm mb-6">
    <div className="flex items-center gap-2 mb-4">
      <div
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: ACCENT + "1a" }}
      >
        <Icon className="h-4 w-4" style={{ color: ACCENT }} />
      </div>
      <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {title}
      </span>
      {helpText && <HelpTooltip>{helpText}</HelpTooltip>}
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

const EventProveedoresSection = ({
  event,
  eventId,
  isViewer = false,
  // Solo lectura (inversionista, P-FRA-13): ve planner, proveedores, costos
  // y comisión como el owner, sin asignar, quitar ni registrar nada.
  // `isViewer` decide qué se VE; `noEscribe` qué se puede TOCAR. El viewer
  // entra en los dos aunque el llamador no pase `soloLectura`.
  soloLectura = false,
  // Lo que el desglose implica y todavía no está capturado. Se DERIVA en
  // EventPage (utils/cotizacionProveedores.js) y se pinta aquí: un evento
  // que ya existía enseña a sus proveedores y su costo sin que nadie haya
  // migrado nada. `onRegistrarPendiente` es lo que lo vuelve permanente.
  pendiente = null,
  onRegistrarPendiente = null,
}) => {
  const navigate = useNavigate();
  const noEscribe = isViewer || soloLectura;
  const {
    selectedVenueId,
    proveedores,
    costos,
    conceptos,
    calendarTimezone,
    metodosPagoConDefaults: metodosPago,
  } = useVenue();
  const { userInfo, user } = useAuth();

  const [plannerPickerOpen, setPlannerPickerOpen] = useState(false);
  const [proveedorPickerOpen, setProveedorPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [unassignCandidate, setUnassignCandidate] = useState(null);
  const [pendingCreate, setPendingCreate] = useState(null); // null | "planner" | "proveedor"
  // Paso intermedio de la asignación (FRA-349): qué servicio del proveedor se
  // contrata. null = no hay asignación a medias.
  const [servicioPick, setServicioPick] = useState(null); // { proveedor, modo, servicioId, varianteId, cantidad }
  // Comisión del planner (FRA-355): al ligarlo se propone la vigente y se
  // ajusta para este evento. null = no hay captura a medias.
  const [comisionPick, setComisionPick] = useState(null); // { proveedor, monto, pagado, fechaPago, nuevoPlanner }
  // Pago rápido a un proveedor sin ir a la sección de costos.
  const [pagoPick, setPagoPick] = useState(null); // { proveedorId, proveedorNombre, costos }

  // Servicios del catálogo agrupados por el proveedor que los da.
  const serviciosPorProveedor = useMemo(() => {
    const map = {};
    (Array.isArray(conceptos) ? conceptos : []).forEach((c) => {
      const pid = proveedorDe(c);
      if (!pid || c.archivedAt) return;
      (map[pid] || (map[pid] = [])).push(c);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
    );
    return map;
  }, [conceptos]);
  const serviciosDe = (pid) => serviciosPorProveedor[pid] || [];

  // Etiqueta del servicio contratado para la lista de asignados: nombre (ya
  // con variante) y la cantidad cuando es más de una.
  const servicioLabelDe = (assignment) => {
    if (!assignment?.servicioNombre) return null;
    const cant = Number(assignment.cantidad) || 1;
    if (cant <= 1) return assignment.servicioNombre;
    const item = (Array.isArray(conceptos) ? conceptos : []).find(
      (c) => c.id === assignment.servicioId
    );
    const unidad = item ? unidadDe(item) : null;
    return unidad
      ? `${assignment.servicioNombre} · ${cant} ${unidad}s`
      : `${assignment.servicioNombre} · × ${cant}`;
  };

  const asignados = useMemo(
    () =>
      Array.isArray(event?.proveedoresAsignados)
        ? event.proveedoresAsignados
        : [],
    [event?.proveedoresAsignados]
  );
  const plannerId = event?.plannerId || null;

  // Proveedores que el desglose implica y que no están asignados. El planner
  // se excluye: su tarjeta es la de arriba y su dinero es comisión.
  const derivados = useMemo(
    () =>
      (pendiente?.proveedoresSinAsignar || []).filter(
        (d) => d.proveedorId !== event?.plannerId
      ),
    [pendiente, event?.plannerId]
  );

  // Costos de este evento agrupados por proveedor. Salen del contexto (ya
  // suscrito), así que ni la fila ni el aviso de desasignar pagan lecturas.
  const costosPorProveedor = useMemo(() => {
    const map = {};
    costosDeEvento(costos, eventId).forEach((c) => {
      if (!c.proveedorId) return;
      (map[c.proveedorId] || (map[c.proveedorId] = [])).push(c);
    });
    return map;
  }, [costos, eventId]);

  // Proveedores eligible as planner (categoria === "planner" && activos, no archivados)
  const plannerCandidates = useMemo(
    () =>
      proveedores
        .filter(esPlannerProveedor)
        .filter((p) => (p.status || "activo") === "activo")
        .filter((p) => !p.archivedAt)
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")),
    [proveedores]
  );

  // Proveedores not yet assigned to this event (no archivados)
  const availableForAssign = useMemo(() => {
    const assignedIds = new Set(asignados.map((a) => a.proveedorId));
    const term = pickerSearch.trim().toLowerCase();
    return proveedores
      .filter((p) => !assignedIds.has(p.id))
      .filter((p) => (p.status || "activo") === "activo")
      .filter((p) => !p.archivedAt)
      .filter((p) =>
        term ? (p.nombre || "").toLowerCase().includes(term) : true
      )
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [proveedores, asignados, pickerSearch]);

  const handleSetPlanner = async (proveedor, contratado = {}) => {
    if (!selectedVenueId || !eventId) return;
    const { servicio = null, varianteId = null, cantidad = 1 } = contratado;
    try {
      const costo = await setPlannerOnEvent({
        venueId: selectedVenueId,
        eventId,
        proveedor,
        userId: user?.uid || null,
        servicio,
        varianteId,
        cantidad,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.EVENT_PLANNER_ASSIGN,
          entity: "event",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
            ...(servicio
              ? { servicioId: servicio.id, servicioNombre: servicio.nombre, varianteId, cantidad }
              : {}),
            ...(costo ? { monto: costo.monto, costoId: costo.costoId } : {}),
          },
        },
        userInfo,
        user
      );
      setPlannerPickerOpen(false);
      setServicioPick(null);
      showAlert(
        costo
          ? `Planner asignado · costo de ${fmtMoneyMXN(costo.monto)} registrado por pagar`
          : "Planner asignado",
        "success"
      );
    } catch (e) {
      console.error(e);
      showAlert("Error al asignar planner: " + e.message, "error");
    }
  };

  const handleClearPlanner = async () => {
    if (!selectedVenueId || !eventId) return;
    try {
      await clearPlannerFromEvent({ venueId: selectedVenueId, eventId });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.EVENT_PLANNER_UNASSIGN,
          entity: "event",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: {
            previousPlannerId: plannerId,
            proveedorNombre: event?.plannerNombre || null,
            eventNombre: event?.nombre || "",
          },
        },
        userInfo,
        user
      );
      showAlert("Planner removido", "success");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  const handleAssign = async (proveedor, contratado = {}) => {
    if (!selectedVenueId || !eventId) return;
    const { servicio = null, varianteId = null, cantidad = 1 } = contratado;
    try {
      const costo = await assignProveedorToEvent({
        venueId: selectedVenueId,
        eventId,
        proveedor,
        userId: user?.uid || null,
        servicio,
        varianteId,
        cantidad,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.EVENT_PROVEEDOR_ASSIGN,
          entity: "event",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
            categoria: proveedor.categoria,
            ...(servicio
              ? { servicioId: servicio.id, servicioNombre: servicio.nombre, varianteId, cantidad }
              : {}),
            ...(costo ? { monto: costo.monto, costoId: costo.costoId } : {}),
          },
        },
        userInfo,
        user
      );
      setPickerSearch("");
      setProveedorPickerOpen(false);
      setServicioPick(null);
      showAlert(
        costo
          ? `Proveedor asignado · costo de ${fmtMoneyMXN(costo.monto)} registrado por pagar`
          : "Proveedor asignado",
        "success"
      );
    } catch (e) {
      console.error(e);
      showAlert("Error al asignar: " + e.message, "error");
    }
  };

  const ejecutarAsignacion = (proveedor, modo, contratado) =>
    modo === "planner"
      ? handleSetPlanner(proveedor, contratado)
      : handleAssign(proveedor, contratado);

  // Punto de entrada de ambos flujos (planner y proveedor). Decide si hace
  // falta preguntar qué servicio se contrata:
  //   - sin servicios en el catálogo → se asigna como siempre, sin costo;
  //   - un solo servicio, sin variantes y por evento → se contrata directo;
  //   - varios servicios, variantes o cantidad → paso intermedio.
  const iniciarAsignacion = (proveedor, modo) => {
    // Un planner no cobra tarifa: cobra comisión. Se propone la del acuerdo
    // vigente y se confirma o ajusta antes de ligarlo.
    //
    // Un planner PURO elegido desde el picker genérico también cobra
    // comisión (enrutarlo por `modo` le registraba además un costo de
    // servicio: dos comisiones). Pero un planner que además da servicios y
    // viene del picker genérico está siendo contratado por su servicio: va
    // por el flujo normal y genera costo de catálogo; su comisión solo nace
    // cuando se le asigna desde el picker de planner.
    if (modo === "planner" || esSoloPlanner(proveedor)) {
      const propuesta = comisionPropuesta(proveedor, getValorEvento(event));
      setPlannerPickerOpen(false);
      setProveedorPickerOpen(false);
      setPickerSearch("");
      setComisionPick({
        proveedor,
        monto: propuesta.monto ? String(propuesta.monto) : "",
        descripcion: propuesta.descripcion,
        pagado: false,
        fechaPago: todayInputValue(),
        // Solo se vuelve EL planner del evento si se eligió como tal (o si
        // el evento todavía no tiene uno).
        nuevoPlanner: modo === "planner" || !plannerId,
      });
      return undefined;
    }
    const servicios = serviciosDe(proveedor.id);
    if (servicios.length === 0) {
      return ejecutarAsignacion(proveedor, modo, {});
    }
    const unico = servicios.length === 1 ? servicios[0] : null;
    if (unico && variantesDe(unico).length === 0 && unidadDe(unico) === "evento") {
      return ejecutarAsignacion(proveedor, modo, {
        servicio: unico,
        varianteId: null,
        cantidad: 1,
      });
    }
    setPlannerPickerOpen(false);
    setProveedorPickerOpen(false);
    setPickerSearch("");
    setServicioPick({
      proveedor,
      modo,
      servicioId: unico ? unico.id : "",
      varianteId: "",
      cantidad: "1",
    });
    return undefined;
  };

  const confirmarServicioPick = () => {
    if (!servicioPick) return;
    const servicio = serviciosDe(servicioPick.proveedor.id).find(
      (c) => c.id === servicioPick.servicioId
    );
    if (!servicio) {
      showAlert("Elige qué servicio se contrata.", "error");
      return;
    }
    const cantidad =
      unidadDe(servicio) === "evento"
        ? 1
        : Number(servicioPick.cantidad) > 0
          ? Number(servicioPick.cantidad)
          : 1;
    ejecutarAsignacion(servicioPick.proveedor, servicioPick.modo, {
      servicio,
      varianteId: servicioPick.varianteId || null,
      cantidad,
    });
  };

  const confirmarComision = async () => {
    if (!comisionPick || !selectedVenueId || !eventId) return;
    const { proveedor, nuevoPlanner } = comisionPick;
    const monto = Number(normalizeMoneyInput(comisionPick.monto)) || 0;
    const fechaPago = comisionPick.pagado
      ? parseDateInputToTimestamp(comisionPick.fechaPago, calendarTimezone)
      : null;
    if (comisionPick.pagado && !fechaPago) {
      showAlert("Fecha de pago inválida.", "error");
      return;
    }
    // Una comisión por planner y evento: registrar una segunda bajaría la
    // utilidad dos veces y la ficha del planner solo mostraría una.
    if (monto > 0 && comisionDeEvento(costos, eventId, proveedor.id)) {
      showAlert("Este planner ya tiene una comisión registrada en el evento.", "error");
      return;
    }
    try {
      if (nuevoPlanner) {
        await setPlannerOnEvent({
          venueId: selectedVenueId,
          eventId,
          proveedor,
          userId: user?.uid || null,
        });
        logBitacora(
          selectedVenueId,
          {
            action: BITACORA_ACTIONS.EVENT_PLANNER_ASSIGN,
            entity: "event",
            entityId: eventId,
            entityName: event?.nombre || "",
            details: { proveedorId: proveedor.id, proveedorNombre: proveedor.nombre },
          },
          userInfo,
          user
        );
      } else if (!asignados.some((a) => a.proveedorId === proveedor.id)) {
        // Planner elegido desde el picker genérico y el evento ya tiene otro
        // planner: se liga como proveedor (sin costo de servicio) y su
        // comisión se registra abajo.
        await assignProveedorToEvent({
          venueId: selectedVenueId,
          eventId,
          proveedor,
          userId: user?.uid || null,
        });
        logBitacora(
          selectedVenueId,
          {
            action: BITACORA_ACTIONS.EVENT_PROVEEDOR_ASSIGN,
            entity: "event",
            entityId: eventId,
            entityName: event?.nombre || "",
            details: {
              proveedorId: proveedor.id,
              proveedorNombre: proveedor.nombre,
              categoria: proveedor.categoria,
            },
          },
          userInfo,
          user
        );
      }
      let registrada = null;
      if (monto > 0) {
        registrada = await registrarComisionDeEvento({
          venueId: selectedVenueId,
          eventId,
          eventData: event,
          proveedor,
          monto,
          pagado: comisionPick.pagado,
          fechaPago,
          metodo: comisionPick.metodo || null,
          userId: user?.uid || null,
        });
        logBitacora(
          selectedVenueId,
          {
            action: BITACORA_ACTIONS.EVENT_COMISION_CREATE,
            entity: "costo",
            entityId: eventId,
            entityName: event?.nombre || "",
            details: {
              proveedorId: proveedor.id,
              proveedorNombre: proveedor.nombre,
              monto,
              pagado: comisionPick.pagado,
              costoId: registrada.costoId,
            },
          },
          userInfo,
          user
        );
      }
      setComisionPick(null);
      showAlert(
        registrada
          ? `Planner asignado · comisión de ${fmtMoneyMXN(monto)} ${comisionPick.pagado ? "pagada" : "por pagar"}`
          : "Planner asignado sin comisión",
        "success"
      );
    } catch (e) {
      console.error(e);
      showAlert("Error al registrar la comisión: " + e.message, "error");
    }
  };

  // Marca como pagados los costos elegidos. Una sola fecha y un solo método
  // para todos: es un pago, no varios. Cada costo se actualiza por separado
  // porque las rules validan doc por doc (fechaPago obligatoria si pagado).
  const confirmarPago = async ({ costos: aPagar, fecha, metodo, comprobante }) => {
    if (!selectedVenueId || aPagar.length === 0) return;
    const fechaPago = parseDateInputToTimestamp(fecha, calendarTimezone);
    if (!fechaPago) {
      showAlert("Fecha de pago inválida.", "error");
      return;
    }
    const nombre = pagoPick?.proveedorNombre || "";
    try {
      for (const c of aPagar) {
        await updateDoc(doc(db, "venues", selectedVenueId, "costos", c.id), {
          estado: "pagado",
          fechaPago,
          // El método solo se pisa si se eligió uno: dejarlo vacío no debe
          // borrar el que ya traía el costo.
          ...(metodo ? { metodo } : {}),
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        });
        logBitacora(
          selectedVenueId,
          {
            action: BITACORA_ACTIONS.COSTO_TOGGLE_ESTADO,
            entity: "costo",
            entityId: eventId,
            entityName: event?.nombre || "",
            details: {
              costoId: c.id,
              estado: "pagado",
              monto: Number(c.monto) || 0,
              concepto: c.concepto || "",
              proveedorNombre: nombre,
            },
          },
          userInfo,
          user
        );
      }
      // El comprobante se sube UNA vez y cuelga de cada costo que pagó: una
      // transferencia puede saldar dos conceptos del mismo proveedor y los
      // dos tienen que poder enseñarla. Borrar uno no borra el archivo
      // mientras otro lo apunte (ver ComprobanteCell).
      if (comprobante?.storagePath) {
        for (const c of aPagar) {
          await addDoc(
            collection(
              db,
              "venues",
              selectedVenueId,
              "calendars",
              "default",
              "events",
              eventId,
              "archivos"
            ),
            {
              label: `Comprobante de pago a ${nombre}`,
              tipo: "comprobante",
              storagePath: comprobante.storagePath,
              filename: comprobante.filename,
              fileSize: comprobante.fileSize,
              mimeType: comprobante.mimeType,
              uploadedAt: Timestamp.now(),
              uploadedBy: user?.uid || null,
              uploadedByName: userInfo?.name || "",
              linkedTo: { kind: "costo", id: c.id },
            }
          );
        }
        logBitacora(
          selectedVenueId,
          {
            action: BITACORA_ACTIONS.COSTO_COMPROBANTE_ATTACH,
            entity: "costo",
            entityId: eventId,
            entityName: event?.nombre || "",
            details: {
              filename: comprobante.filename,
              costos: aPagar.length,
              proveedorNombre: nombre,
            },
          },
          userInfo,
          user
        );
      }

      const total = aPagar.reduce((s2, c) => s2 + (Number(c.monto) || 0), 0);
      setPagoPick(null);
      showAlert(
        `Pago de ${fmtMoneyMXN(total)} a ${nombre} registrado`,
        "success"
      );
    } catch (err) {
      console.error(err);
      showAlert(`No se pudo registrar el pago. [${err.message}]`, "error");
    }
  };

  const handleUnassign = async () => {
    if (!unassignCandidate || !selectedVenueId || !eventId) return;
    try {
      const autoCosto = (costosPorProveedor[unassignCandidate.proveedorId] || []).find(
        (c) => c.origen === "proveedor_auto"
      );
      const { costoConservado } = await unassignProveedorFromEvent({
        venueId: selectedVenueId,
        eventId,
        proveedorId: unassignCandidate.proveedorId,
        autoCosto,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.EVENT_PROVEEDOR_UNASSIGN,
          entity: "event",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: {
            proveedorId: unassignCandidate.proveedorId,
            proveedorNombre: unassignCandidate.proveedorNombre,
          },
        },
        userInfo,
        user
      );
      // If this was the planner, planner is cleared automatically by the helper
      setUnassignCandidate(null);
      showAlert(
        costoConservado
          ? "Proveedor removido · su costo registrado se conserva"
          : "Proveedor removido",
        costoConservado ? "warning" : "success"
      );
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  // Non-planner assignments (the planner is shown in its own card if set)
  const otrosAsignados = asignados.filter((a) => a.proveedorId !== plannerId);
  const plannerEntry = asignados.find((a) => a.proveedorId === plannerId);
  const plannerDoc = plannerId ? proveedores.find((p) => p.id === plannerId) || null : null;
  const comisionPlanner = plannerId ? comisionDeEvento(costos, eventId, plannerId) : null;
  const [pagandoComision, setPagandoComision] = useState(null); // { fecha, metodo }
  const marcarComisionPagada = async ({ fecha: fechaInput, metodo = "" } = {}) => {
    if (!comisionPlanner) return;
    const fechaPago = parseDateInputToTimestamp(fechaInput, calendarTimezone);
    if (!fechaPago) {
      showAlert("Fecha de pago inválida.", "error");
      return;
    }
    try {
      await updateDoc(doc(db, "venues", selectedVenueId, "costos", comisionPlanner.id), {
        estado: "pagado",
        fechaPago,
        ...(metodo ? { metodo } : {}),
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.COSTO_TOGGLE_ESTADO,
          entity: "costo",
          entityId: comisionPlanner.id,
          entityName: comisionPlanner.concepto || "",
          details: { estado: "pagado", monto: Number(comisionPlanner.monto) || 0, fechaPago: fechaInput, ...(metodo ? { metodo } : {}) },
        },
        userInfo,
        user
      );
      setPagandoComision(null);
      showAlert("Comisión marcada como pagada", "success");
    } catch (e) {
      showAlert("No se pudo actualizar: " + e.message, "error");
    }
  };

  return (
    <>
      {/* Planner card */}
      <SectionCard
        icon={SparklesIcon}
        title="Planner"
        helpText={
          noEscribe
            ? null
            : (
              <>
                Asigna un planner del <strong>Directorio</strong>. Al ligarlo se
                propone su comisión (monto fijo o porcentaje del valor del
                evento, según su acuerdo) y la puedes ajustar para este evento.
                También puedes crear uno desde aquí con "Crear planner nuevo".
              </>
            )
        }
        action={
          !noEscribe &&
          plannerId && (
            <button
              type="button"
              onClick={handleClearPlanner}
              className="text-xs text-zinc-500 hover:text-red-500"
            >
              Quitar planner
            </button>
          )
        }
      >
        {plannerEntry ? (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
            <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-zinc-900">
                  {plannerEntry.proveedorNombre}
                </span>
                <Badge color="indigo">Planner</Badge>
              </div>
              {servicioLabelDe(plannerEntry) && (
                <div className="text-xs text-zinc-500 truncate">
                  {servicioLabelDe(plannerEntry)}
                </div>
              )}
              {/* Comisión de este evento (FRA-355) */}
              {!isViewer && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                  {comisionPlanner ? (
                    <>
                      <span className="text-zinc-600">
                        Comisión <span className="font-semibold text-zinc-900">{fmtMoneyMXN(comisionPlanner.monto)}</span>
                        {comisionPlanner.comisionAcuerdo && (
                          <span className="text-zinc-400"> · {describeAcuerdo(comisionPlanner.comisionAcuerdo)}</span>
                        )}
                      </span>
                      {comisionPlanner.estado === "pagado" ? (
                        <Badge color="emerald">
                          Pagada {formatCalendarDate(comisionPlanner.fechaPago, calendarTimezone, { style: "short" }) || ""}
                          {comisionPlanner.metodo ? ` · ${comisionPlanner.metodo}` : ""}
                        </Badge>
                      ) : soloLectura ? (
                        <Badge color="amber">Por pagar</Badge>
                      ) : pagandoComision != null ? (
                        <span className="inline-flex items-center gap-1.5">
                          <input
                            type="date"
                            value={pagandoComision.fecha}
                            onChange={(e) => setPagandoComision({ ...pagandoComision, fecha: e.target.value })}
                            className="rounded-lg border border-zinc-300 bg-white px-2 py-1 text-xs"
                          />
                          <MetodoPagoField
                            value={pagandoComision.metodo}
                            onChange={(v) => setPagandoComision({ ...pagandoComision, metodo: v })}
                            metodosPago={metodosPago}
                            className="rounded-lg border border-zinc-300 bg-white px-2 py-1 text-xs"
                            vacio="Método"
                          />
                          <button
                            type="button"
                            onClick={() => marcarComisionPagada(pagandoComision)}
                            className="px-2 py-1 rounded-lg text-xs font-semibold text-white"
                            style={{ backgroundColor: ACCENT }}
                          >
                            Confirmar
                          </button>
                          <button type="button" onClick={() => setPagandoComision(null)} className="text-zinc-400 hover:text-zinc-600">Cancelar</button>
                        </span>
                      ) : (
                        <button type="button" onClick={() => setPagandoComision({ fecha: todayInputValue(), metodo: comisionPlanner.metodo || "" })} title="Marcar como pagada">
                          <Badge color="amber">Por pagar · marcar pagada</Badge>
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-zinc-400">Sin comisión registrada.</span>
                      {plannerDoc && !soloLectura && (
                        <button
                          type="button"
                          onClick={() => {
                            const propuesta = comisionPropuesta(plannerDoc, getValorEvento(event));
                            setComisionPick({
                              proveedor: plannerDoc,
                              monto: propuesta.monto ? String(propuesta.monto) : "",
                              descripcion: propuesta.descripcion,
                              pagado: false,
                              fechaPago: todayInputValue(),
                              metodo: "",
                              nuevoPlanner: false,
                            });
                          }}
                          className="font-semibold hover:underline"
                          style={{ color: ACCENT }}
                        >
                          Registrar comisión
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {!isViewer && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/dashboard/proveedores/${plannerEntry.proveedorId}`,
                    {
                      state: {
                        from: "evento",
                        eventId,
                        eventTab: "operacion",
                      },
                    }
                  )
                }
                className="text-zinc-400 hover:text-zinc-600 p-1.5"
                title="Ver proveedor"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </button>
            )}
            </div>
            <ProveedorEventDepositos
              venueId={selectedVenueId}
              proveedorId={plannerEntry.proveedorId}
              eventId={eventId}
              isViewer={isViewer}
              soloLectura={soloLectura}
              onNavigateToDepositos={() =>
                navigate(
                  `/dashboard/proveedores/${plannerEntry.proveedorId}?tab=depositos`,
                  {
                    state: {
                      from: "evento",
                      eventId,
                      eventTab: "operacion",
                    },
                  }
                )
              }
            />
            <ProveedorEventComments
              venueId={selectedVenueId}
              proveedorId={plannerEntry.proveedorId}
              proveedorNombre={plannerEntry.proveedorNombre}
              eventId={eventId}
              eventNombre={event?.nombre || null}
              isViewer={isViewer}
              soloLectura={soloLectura}
            />
          </div>
        ) : noEscribe ? (
          <div className="text-sm text-zinc-400 italic">Sin planner asignado.</div>
        ) : plannerCandidates.length === 0 ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-zinc-500">
              No hay planners en el directorio del venue todavía.
            </span>
            <button
              type="button"
              onClick={() => setPendingCreate("planner")}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              Crear planner nuevo
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPlannerPickerOpen(true)}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
            style={{ backgroundColor: ACCENT }}
          >
            <PlusIcon className="h-4 w-4" />
            Asignar planner
          </button>
        )}
      </SectionCard>

      {/* Otros proveedores card */}
      <SectionCard
        icon={UserGroupIcon}
        title="Proveedores asignados"
        helpText={
          noEscribe
            ? null
            : (
              <>
                Asigna proveedores del <strong>Directorio</strong>. Al elegir qué
                servicio le contratas, su costo se registra solo, por pagar.
                También puedes crear uno aquí mismo desde el buscador.
              </>
            )
        }
        action={
          !noEscribe && (
            <button
              type="button"
              onClick={() => setProveedorPickerOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              Asignar
            </button>
          )
        }
      >
        {otrosAsignados.length === 0 && derivados.length === 0 ? (
          <div className="text-sm text-zinc-400 italic py-2">
            Aún no hay proveedores asignados a este evento.
          </div>
        ) : (
          <div className="space-y-2">
            {otrosAsignados.map((a) => (
              <AssignedProveedorRow
                key={a.proveedorId}
                assignment={a}
                venueId={selectedVenueId}
                eventId={eventId}
                eventNombre={event?.nombre || null}
                isViewer={isViewer}
                soloLectura={soloLectura}
                costos={costosPorProveedor[a.proveedorId]}
                estimado={pendiente?.estimadoPorProveedor?.[a.proveedorId] || 0}
                servicioLabel={servicioLabelDe(a)}
                onPagar={(porPagarCostos) =>
                  setPagoPick({
                    proveedorId: a.proveedorId,
                    proveedorNombre: a.proveedorNombre,
                    costos: porPagarCostos,
                  })
                }
                onUnassign={() => setUnassignCandidate(a)}
                onOpen={() =>
                  navigate(`/dashboard/proveedores/${a.proveedorId}`, {
                    state: {
                      from: "evento",
                      eventId,
                      eventTab: "operacion",
                    },
                  })
                }
              />
            ))}

            {/* Derivados del desglose: la cotización dice que participan, pero
                nadie los ha asignado. Se pintan igual de reales porque lo son
                — lo único que falta es persistirlos, y para eso está el botón
                de abajo. Sin esto, un evento viejo se veía vacío. */}
            {derivados.map((d) => (
              <ProveedorDerivadoRow
                key={d.proveedorId}
                pendiente={d}
                isViewer={noEscribe}
                onAsignar={onRegistrarPendiente}
              />
            ))}
          </div>
        )}

        {!noEscribe && pendiente?.hayPendiente && onRegistrarPendiente && (
          <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm dark:border-zinc-700/60 dark:bg-zinc-800/40">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-zinc-600 dark:text-zinc-300">
                {pendiente.montoEstimado > 0 ? (
                  <>
                    La cotización implica{" "}
                    <span className="font-semibold">
                      {fmtMoneyMXN(pendiente.montoEstimado)}
                    </span>{" "}
                    de costos que todavía no están registrados.
                  </>
                ) : (
                  <>La cotización implica proveedores que no están asignados.</>
                )}
              </span>
              <button
                type="button"
                onClick={() => onRegistrarPendiente()}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: ACCENT }}
              >
                {pendiente.montoEstimado > 0
                  ? "Asignar todos y registrar costos"
                  : "Asignar todos"}
              </button>
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              {pendiente.montoEstimado > 0 ? (
                <>
                  Los asigna al evento y convierte lo estimado en costos por
                  pagar. El total del evento no cambia —ya está contado—: lo
                  que gana es que entran a Cuentas y los puedes marcar pagados.
                </>
              ) : (
                <>Los deja asignados al evento.</>
              )}
            </div>
          </div>
        )}
      </SectionCard>

      {pagoPick && (
        <PagoProveedorDialog
          proveedorNombre={pagoPick.proveedorNombre}
          costos={pagoPick.costos}
          metodosPago={metodosPago || []}
          storagePrefix={
            selectedVenueId && eventId
              ? `venues/${selectedVenueId}/eventos/${eventId}/archivos`
              : null
          }
          onCancel={() => setPagoPick(null)}
          onConfirm={confirmarPago}
        />
      )}

      {/* Planner picker dialog */}
      {plannerPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setPlannerPickerOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: ACCENT + "1a" }}
                >
                  <SparklesIcon className="h-4 w-4" style={{ color: ACCENT }} />
                </div>
                <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                  Asignar planner
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setPlannerPickerOpen(false)}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {plannerCandidates.length === 0 ? (
                <div className="text-sm text-zinc-500 py-4">
                  Aún no hay proveedores con categoría <strong>Planner</strong>.
                </div>
              ) : (
                <div className="space-y-1">
                  {plannerCandidates.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => iniciarAsignacion(p, "planner")}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 border border-transparent hover:border-zinc-200 dark:hover:border-zinc-700"
                    >
                      <div className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                        {p.nombre}
                      </div>
                      {p.ratingCount > 0 && (
                        <div className="text-xs text-zinc-500">
                          ★ {Number(p.ratingPromedio).toFixed(1)} ({p.ratingCount})
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setPlannerPickerOpen(false);
                  setPendingCreate("planner");
                }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1b4d2e] hover:underline"
              >
                <PlusIcon className="h-4 w-4" />
                Crear planner nuevo
              </button>
              <button
                type="button"
                onClick={() => setPlannerPickerOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proveedor picker dialog */}
      {proveedorPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              setProveedorPickerOpen(false);
              setPickerSearch("");
            }}
          />
          <div className="relative w-full max-w-md rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: ACCENT + "1a" }}
                >
                  <UserGroupIcon
                    className="h-4 w-4"
                    style={{ color: ACCENT }}
                  />
                </div>
                <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                  Asignar proveedor al evento
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setProveedorPickerOpen(false);
                  setPickerSearch("");
                }}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="relative mb-3">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <input
                  type="text"
                  autoFocus
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Buscar por nombre..."
                  className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-9 pr-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                />
              </div>
              {availableForAssign.length === 0 ? (
                <div className="text-sm text-zinc-500 py-4 text-center">
                  {proveedores.length === 0
                    ? "Aún no hay proveedores en el directorio."
                    : "Sin proveedores disponibles."}
                </div>
              ) : (
                <div className="space-y-1">
                  {availableForAssign.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => iniciarAsignacion(p, "proveedor")}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 border border-transparent hover:border-zinc-200 dark:hover:border-zinc-700"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                          {p.nombre}
                        </span>
                        {p.isPlanner && <Badge color="indigo">Planner</Badge>}
                      </div>
                      {p.ratingCount > 0 && (
                        <div className="text-xs text-zinc-500">
                          ★ {Number(p.ratingPromedio).toFixed(1)} ({p.ratingCount})
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setProveedorPickerOpen(false);
                  setPickerSearch("");
                  setPendingCreate("proveedor");
                }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1b4d2e] hover:underline"
              >
                <PlusIcon className="h-4 w-4" />
                Crear proveedor nuevo
              </button>
              <button
                type="button"
                onClick={() => {
                  setProveedorPickerOpen(false);
                  setPickerSearch("");
                }}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unassign confirmation */}
      <Alert
        open={!!unassignCandidate}
        onClose={() => setUnassignCandidate(null)}
      >
        <div className="font-semibold mb-2">
          ¿Remover a "{unassignCandidate?.proveedorNombre}" de este evento?
        </div>
        <div className="mb-4 text-sm text-zinc-600">
          El proveedor en sí no se elimina, sólo se quita de este evento. Las
          reseñas existentes vinculadas al evento se conservan.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setUnassignCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleUnassign}>
            Remover
          </Button>
        </div>
      </Alert>

      {/* Inline create — opened from the picker dialogs */}
      <ProveedorCreateDialog
        open={pendingCreate !== null}
        initialCategoria={pendingCreate === "planner" ? "planner" : ""}
        onClose={() => setPendingCreate(null)}
        onCreated={async (_id, newProveedor) => {
          try {
            // Recién creado no tiene servicios en el catálogo, así que se
            // asigna directo; el picker de servicio no aplica todavía.
            if (pendingCreate === "planner" || pendingCreate === "proveedor") {
              await iniciarAsignacion(newProveedor, pendingCreate);
            }
          } finally {
            setPendingCreate(null);
          }
        }}
      />

      {/* Comisión del planner (FRA-355) */}
      {comisionPick && (
        <ComisionDialog
          pick={comisionPick}
          metodosPago={metodosPago}
          valorEvento={getValorEvento(event)}
          onChange={(patch) => setComisionPick((prev) => (prev ? { ...prev, ...patch } : prev))}
          onCancel={() => setComisionPick(null)}
          onConfirm={confirmarComision}
        />
      )}

      {/* Paso intermedio: qué servicio se contrata (FRA-349) */}
      {servicioPick && (
        <ServicioPickerDialog
          pick={servicioPick}
          servicios={serviciosDe(servicioPick.proveedor.id)}
          onChange={(patch) => setServicioPick((prev) => (prev ? { ...prev, ...patch } : prev))}
          onCancel={() => setServicioPick(null)}
          onConfirm={confirmarServicioPick}
        />
      )}

    </>
  );
};

// Comisión del planner para ESTE evento (FRA-355): se propone la del
// acuerdo vigente, se ajusta si se negoció distinto, y se dice si ya se
// pagó. Con monto cero el planner queda ligado sin comisión.
const ComisionDialog = ({ pick, valorEvento, metodosPago = [], onChange, onCancel, onConfirm }) => {
  const { proveedor, monto, descripcion, pagado, fechaPago, metodo = "" } = pick;
  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";
  const m = Number(normalizeMoneyInput(monto)) || 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: ACCENT + "1a" }}>
              <SparklesIcon className="h-4 w-4" style={{ color: ACCENT }} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Comisión del planner</h2>
              <div className="text-xs text-zinc-500">{proveedor.nombre}</div>
            </div>
          </div>
          <button type="button" onClick={onCancel} className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/50 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-300">
            {descripcion}
            {valorEvento > 0 && (
              <span className="text-zinc-400"> · Valor del evento: {fmtMoneyMXN(valorEvento)}</span>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Comisión para este evento</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={fmtMXNInput(monto)}
                onChange={(e) => onChange({ monto: normalizeMoneyInput(e.target.value) })}
                className={`${inputClass} pl-7`}
                placeholder="0"
                autoFocus
              />
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Se registra como costo del evento y baja su utilidad. Con $0 el planner queda ligado sin comisión.
            </p>
          </div>
          {m > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                <input type="checkbox" checked={!!pagado} onChange={(e) => onChange({ pagado: e.target.checked })} />
                Ya se pagó
              </label>
              {pagado && (
                <>
                  <input
                    type="date"
                    value={fechaPago}
                    onChange={(e) => onChange({ fechaPago: e.target.value })}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1.5 text-sm"
                    title="Fecha en que salió el dinero"
                  />
                  <MetodoPagoField
                    value={metodo}
                    onChange={(v) => onChange({ metodo: v })}
                    metodosPago={metodosPago}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1.5 text-sm"
                    vacio="Método"
                  />
                </>
              )}
              {!pagado && <span className="text-xs text-zinc-400">Queda por pagar, en Cuentas → Por pagar.</span>}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 transition">
            Cancelar
          </button>
          <button type="button" onClick={onConfirm} className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90" style={{ backgroundColor: ACCENT }}>
            {pick.nuevoPlanner ? "Asignar planner" : "Registrar comisión"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Paso intermedio de la asignación (FRA-349): qué servicio del proveedor se
// contrata, con qué variante y en qué cantidad. Muestra el costo que va a
// caer al evento antes de confirmar: la tarifa del servicio × la cantidad.
// Los planners no pasan por aquí — cobran comisión (ComisionDialog).
const ServicioPickerDialog = ({
  pick,
  servicios,
  onChange,
  onCancel,
  onConfirm,
}) => {
  const { proveedor, modo, servicioId, varianteId, cantidad } = pick;
  const servicio = servicios.find((c) => c.id === servicioId) || null;
  const variantes = servicio ? variantesDe(servicio) : [];
  const variante = servicio ? varianteDe(servicio, varianteId) : null;
  const unidad = servicio ? unidadDe(servicio) : "evento";
  const pideCantidad = !!servicio && unidad !== "evento";
  const cant = pideCantidad ? (Number(cantidad) > 0 ? Number(cantidad) : 1) : 1;
  const total = servicio ? costoDeVariante(servicio, variante, cant) : 0;

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <div className="flex items-center gap-3">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <BanknotesIcon className="h-4 w-4" style={{ color: ACCENT }} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                {modo === "planner" ? "Asignar planner" : "Asignar proveedor"}
              </h2>
              <div className="text-xs text-zinc-500">{proveedor.nombre}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">
              Servicio que se contrata
            </label>
            {servicios.length === 1 ? (
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {servicios[0].nombre}
              </div>
            ) : (
              <select
                value={servicioId}
                onChange={(e) => onChange({ servicioId: e.target.value, varianteId: "" })}
                className={inputClass}
              >
                <option value="">Elige un servicio…</option>
                {servicios.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            )}
          </div>

          {variantes.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Variante
              </label>
              <select
                value={varianteId}
                onChange={(e) => onChange({ varianteId: e.target.value })}
                className={inputClass}
              >
                <option value="">Sin variante</option>
                {variantes.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {pideCantidad && (
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Cantidad
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={cantidad}
                  onChange={(e) => onChange({ cantidad: e.target.value })}
                  className={`${inputClass} max-w-[8rem]`}
                />
                <span className="text-sm text-zinc-500">
                  {unidad}{cant === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/50 px-3 py-2 text-sm">
            {!servicio ? (
              <span className="text-zinc-400">Elige un servicio para ver su costo.</span>
            ) : !(total > 0) ? (
              <span className="text-zinc-600 dark:text-zinc-300">
                Sin costo automático: el servicio no tiene tarifa capturada.
              </span>
            ) : (
              <>
                Se registrará un costo de{" "}
                <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                  {fmtMoneyMXN(total)}
                </span>{" "}
                por pagar.
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!servicio}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            Asignar
          </button>
        </div>
      </div>
    </div>
  );
};

// Pagarle a un proveedor sin salir del evento. El costo es atómico —o está
// pagado o no— así que esto no parte montos: marca los costos elegidos como
// pagados con UNA fecha y UN método. Un pago parcial se resuelve editando el
// monto en Costos del evento, que es donde vive esa edición.
//
// La fecha se pregunta, no se asume: le pagaste al DJ el viernes y lo
// registras el lunes, y el egreso tiene que caer en viernes (FRA-342). Es la
// misma regla que EventCostosSection.
const PagoProveedorDialog = ({
  proveedorNombre,
  costos,
  metodosPago,
  storagePrefix,
  onCancel,
  onConfirm,
}) => {
  const [elegidos, setElegidos] = useState(() => costos.map((c) => c.id));
  const [fecha, setFecha] = useState(todayInputValue());
  const [metodo, setMetodo] = useState("");
  const [comprobante, setComprobante] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const total = costos
    .filter((c) => elegidos.includes(c.id))
    .reduce((s, c) => s + (Number(c.monto) || 0), 0);

  const toggle = (id) =>
    setElegidos((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <div className="flex items-center gap-3">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <BanknotesIcon className="h-4 w-4" style={{ color: ACCENT }} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Registrar pago
              </h2>
              <div className="text-xs text-zinc-500">{proveedorNombre}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <div className="text-xs font-medium text-zinc-500 mb-1.5">
              {costos.length === 1 ? "Qué se paga" : "Qué se paga"}
            </div>
            <div className="space-y-1.5">
              {costos.map((c) => {
                const activo = elegidos.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c.id)}
                    className={`w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition ${
                      activo
                        ? "border-zinc-300 bg-white dark:border-zinc-600 dark:bg-zinc-800"
                        : "border-zinc-200 bg-zinc-50 opacity-60 dark:border-zinc-700 dark:bg-zinc-800/40"
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        activo ? "border-transparent" : "border-zinc-300 bg-white"
                      }`}
                      style={activo ? { backgroundColor: ACCENT } : undefined}
                    >
                      {activo && <CheckIcon className="h-3 w-3 text-white" />}
                    </span>
                    <span className="flex-1 min-w-0 text-sm text-zinc-800 truncate dark:text-zinc-200">
                      {c.concepto || "Costo sin concepto"}
                    </span>
                    <span className="shrink-0 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                      {fmtMoneyMXN(Number(c.monto) || 0)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Fecha en que salió el dinero
              </label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Método
              </label>
              {metodosPago.length > 0 ? (
                <select
                  value={metodo}
                  onChange={(e) => setMetodo(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Sin método</option>
                  {metodosPago.map((m) => (
                    <option key={m.id || m.nombre} value={m.nombre}>
                      {m.nombre}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={metodo}
                  onChange={(e) => setMetodo(e.target.value)}
                  placeholder="Transferencia, efectivo…"
                  className={inputClass}
                />
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">
              Comprobante <span className="font-normal text-zinc-400">(opcional)</span>
            </label>
            {comprobante ? (
              <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800">
                <PaperClipIcon className="h-4 w-4 shrink-0 text-zinc-400" />
                <span className="flex-1 min-w-0 truncate text-zinc-700 dark:text-zinc-200">
                  {comprobante.filename}
                </span>
                <button
                  type="button"
                  onClick={() => setComprobante(null)}
                  className="shrink-0 text-zinc-400 hover:text-red-500"
                  title="Quitar"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <DocumentUploader
                storagePathPrefix={storagePrefix}
                buttonLabel="Adjuntar comprobante"
                withDownloadUrl={false}
                disabled={!storagePrefix}
                onUploaded={setComprobante}
              />
            )}
            {elegidos.length > 1 && comprobante && (
              <p className="mt-1 text-xs text-zinc-500">
                El mismo comprobante queda en los {elegidos.length} costos.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
          <div className="text-sm">
            <span className="text-zinc-500">Total: </span>
            <span className="font-semibold text-zinc-900 dark:text-zinc-100">
              {fmtMoneyMXN(total)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={elegidos.length === 0 || !fecha || guardando}
              onClick={async () => {
                setGuardando(true);
                await onConfirm({
                  costos: costos.filter((c) => elegidos.includes(c.id)),
                  fecha,
                  metodo: metodo.trim() || null,
                  comprobante,
                });
                setGuardando(false);
              }}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
            >
              {guardando ? "Guardando…" : "Marcar pagado"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Un proveedor que existe en el evento porque el desglose lo dice, no porque
// alguien lo haya asignado. Se ve como los demás —participa igual— pero dice
// de dónde salió y que su costo todavía es una estimación del catálogo.
const ProveedorDerivadoRow = ({ pendiente, isViewer, onAsignar }) => (
  <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/30">
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-zinc-900 truncate dark:text-zinc-100">
          {pendiente.proveedorNombre || pendiente.proveedorId}
        </div>
        <div className="text-xs text-zinc-600 truncate dark:text-zinc-400">
          {pendiente.concepto}
          {pendiente.cantidad > 1 ? ` · × ${pendiente.cantidad}` : ""}
        </div>
        <div className="mt-0.5 text-xs text-zinc-400">
          Sale de la cotización · sin asignar
        </div>
      </div>
      {pendiente.monto > 0 && (
        <span className="inline-flex shrink-0 items-center rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600 ring-1 ring-inset ring-zinc-300 dark:bg-zinc-700 dark:text-zinc-200 dark:ring-zinc-600">
          {fmtMoneyMXN(pendiente.monto)} estimado
        </span>
      )}
      {/* La fila dice "sin asignar": la acción para dejar de estarlo tiene
          que vivir en la misma fila, no en un botón general al pie. */}
      {!isViewer && onAsignar && (
        <button
          type="button"
          onClick={() => onAsignar(pendiente.proveedorId)}
          className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
          style={{ backgroundColor: ACCENT }}
          title={
            pendiente.monto > 0
              ? `Asignar a ${pendiente.proveedorNombre} y registrar su costo de ${fmtMoneyMXN(pendiente.monto)} por pagar`
              : `Asignar a ${pendiente.proveedorNombre} a este evento`
          }
        >
          Asignar
        </button>
      )}
    </div>
  </div>
);

const AssignedProveedorRow = ({
  assignment,
  venueId,
  eventId,
  eventNombre,
  isViewer,
  soloLectura = false,
  costos,
  estimado = 0,
  servicioLabel = null,
  onUnassign,
  onOpen,
  onPagar = null,
}) => {
  const navigate = useNavigate();
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const costosDelProveedor = Array.isArray(costos) ? costos : [];
  const pagado = costosDelProveedor
    .filter((c) => c.estado === "pagado")
    .reduce((s, c) => s + (Number(c.monto) || 0), 0);
  const porPagar = costosDelProveedor
    .filter((c) => (c.estado || "por_pagar") === "por_pagar")
    .reduce((s, c) => s + (Number(c.monto) || 0), 0);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-zinc-900 truncate">
            {assignment.proveedorNombre}
          </div>
          {servicioLabel && (
            <div className="text-xs text-zinc-600 truncate">{servicioLabel}</div>
          )}
          {assignment.categoria && (
            <div className="text-xs text-zinc-500 truncate">
              {assignment.categoria}
              {assignment.subcategoria
                ? ` · ${assignment.subcategoria}`
                : ""}
            </div>
          )}
          {/* Lo que aparece solo tiene que explicarse solo: este proveedor
              lo puso la cotización, no una persona. */}
          {assignment.origen === "cotizacion" && (
            <div className="mt-0.5 text-xs text-zinc-400">
              Asignado desde la cotización
            </div>
          )}
        </div>
        {!isViewer && (
          <div className="flex gap-0.5">
            <button
              type="button"
              onClick={onOpen}
              className="text-zinc-400 hover:text-zinc-600 p-1.5"
              title="Ver proveedor"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </button>
            {!soloLectura && (
            <button
              type="button"
              onClick={onUnassign}
              className="text-zinc-400 hover:text-red-500 p-1.5"
              title="Quitar del evento"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
            )}
          </div>
        )}
      </div>
      {!isViewer && (costosDelProveedor.length > 0 || estimado > 0) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {estimado > 0 && (
            <span className="inline-flex items-center rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600 ring-1 ring-inset ring-zinc-300">
              {fmtMoneyMXN(estimado)} estimado
            </span>
          )}
          {porPagar > 0 && (
            <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
              {fmtMoneyMXN(porPagar)} por pagar
            </span>
          )}
          {/* Si el renglón dice que le debes, la forma de dejar de deberle
              tiene que estar en el renglón. */}
          {porPagar > 0 && onPagar && !soloLectura && (
            <button
              type="button"
              onClick={() =>
                onPagar(
                  costosDelProveedor.filter(
                    (c) => (c.estado || "por_pagar") === "por_pagar"
                  )
                )
              }
              className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              Registrar pago
            </button>
          )}
          {pagado > 0 && (
            <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
              {fmtMoneyMXN(pagado)} pagado
            </span>
          )}
          {costosDelProveedor.length > 0 && (
            <span className="text-xs text-zinc-400">
              El detalle, más abajo en Costos del evento
            </span>
          )}
        </div>
      )}
      {/* Depósitos en garantía y comentarios, plegados.
          Iban siempre abiertos, y como casi siempre están vacíos la fila
          gastaba cuatro renglones en decir "sin depósito" y "0 comentarios"
          mientras callaba lo que sí importa —cuánto le debes—. De paso, su
          botón "Registrar" competía por nombre con el de pago.
          Plegado también significa que sus dos onSnapshot no se abren hasta
          que alguien los pide: con 8 proveedores eran 16 suscripciones vivas
          para pintar vacíos. */}
      {!isViewer && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setDetalleAbierto((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-zinc-400 hover:text-zinc-600"
          >
            <ChevronRightIcon
              className={`h-3.5 w-3.5 transition-transform ${
                detalleAbierto ? "rotate-90" : ""
              }`}
            />
            Depósitos y comentarios
          </button>
          {detalleAbierto && (
            <>
              <ProveedorEventDepositos
                venueId={venueId}
                proveedorId={assignment.proveedorId}
                eventId={eventId}
                isViewer={isViewer}
                soloLectura={soloLectura}
                onNavigateToDepositos={() =>
                  navigate(
                    `/dashboard/proveedores/${assignment.proveedorId}?tab=depositos`,
                    {
                      state: {
                        from: "evento",
                        eventId,
                        eventTab: "operacion",
                      },
                    }
                  )
                }
              />
              <ProveedorEventComments
                venueId={venueId}
                proveedorId={assignment.proveedorId}
                proveedorNombre={assignment.proveedorNombre}
                eventId={eventId}
                eventNombre={eventNombre}
                isViewer={isViewer}
                soloLectura={soloLectura}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EventProveedoresSection;
